"use client";
import { useEffect, useRef, useState } from "react";
import * as stylex from "@stylexjs/stylex";
import { ReplayIframe } from "./replay-iframe";
import { ReplayDeliveryBadge } from "./replay-delivery-badge";
import type { ReplayTimingController } from "./playback/use-replay-timing";
import { EmbedFullscreenButton } from "../viewer/embed-fullscreen-button";
import { replayStyles as styles } from "../viewer/replay.styles";
import {
  type CoworldReplayFrameProps,
  type ReplaySession,
  type Playback,
} from "./replay-frame.types";
import { controlStyles } from "../viewer/replay-control.styles";
import { frameSize } from "./replay-frame-layout";

export function ReadyReplayFrame({
  session,
  playback,
  badge,
  letterboxBg,
  onPause,
  ...props
}: CoworldReplayFrameProps & {
  session: ReplaySession;
  playback: Playback;
  badge: ReplayTimingController["badge"];
  letterboxBg: string | null;
  onPause: () => void;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null,
  );
  const { viewerSize, bare, showLoadingLabel = true } = props;
  useEffect(() => {
    if (!viewerSize) return;

    const target = frame.current;

    if (target === null) return;

    const measure = () =>
      setSize({ width: target.clientWidth, height: target.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(target);
    return () => observer.disconnect();
  }, [viewerSize]);
  const viewerScale =
    viewerSize && size && size.width > 0 && size.height > 0
      ? Math.min(size.width / viewerSize.width, size.height / viewerSize.height)
      : 1;
  const iframe = (
    <ReplayIframe
      {...props}
      session={session}
      letterboxBg={letterboxBg}
      playback={playback}
    />
  );

  return (
    <div
      ref={frame}
      {...stylex.props(
        styles.frame,
        !bare && styles.bordered,
        bare ? styles.page : styles.viewer,
      )}
      style={frameSize(props)}
    >
      {!playback.painted && (
        <output
          {...stylex.props(styles.overlay, bare ? styles.page : styles.surface)}
        >
          <span aria-hidden="true" {...stylex.props(styles.spinner)} />
          <span
            {...stylex.props(
              styles.text,
              !showLoadingLabel && styles.visuallyHidden,
            )}
          >
            Loading replay...
          </span>
        </output>
      )}
      {viewerSize ? (
        <div {...stylex.props(styles.scaledViewer)}>
          <div
            style={{
              width: viewerSize.width,
              height: viewerSize.height,
              transform: "scale(" + viewerScale + ")",
              transformOrigin: "center center",
            }}
          >
            {iframe}
          </div>
        </div>
      ) : (
        iframe
      )}
      <EmbedFullscreenButton
        targetRef={frame}
        label={props.title ?? "replay"}
      />
      {props.chrome === "controls" && (
        <button
          type="button"
          onClick={onPause}
          aria-label="Pause replay"
          title="Pause replay"
          {...stylex.props(controlStyles.control, controlStyles.pause)}
        >
          <PauseGlyph />
        </button>
      )}
      <ReplayDeliveryBadge
        enabled={props.diagnostics !== false}
        badge={badge}
      />
    </div>
  );
}

function PauseGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}
